// Note: This patch modifies the jsonNodeTree
// Patches applied after this one should be applied in a new tree iteration
import * as DomTypeUtils from '@packages/systems/core/utils/DomTypeUtils';
import {rteHtmlToJsonNodes} from '@packages/systems/core/utils/JsonDomUtils';
import {type ExtraContext} from '../ExtraContext';

// Expand legacy TextHolder nodes to use the new rte-next format with type 3 text nodes
export default (
  context: any,
  {reportWarning}: {reportWarning?: ExtraContext['reportWarning']} = {}
) => {
  const VALID_TYPOGRAPHIC_CONTENT_NODES = ['a', 'strong', 'em', 'br', 'span'];
  const {
    node: {jsonNode},
    tree: {jsonNodes},
    // eslint-disable-next-line webflow/deprecated
    fn: {__DEPRECATED__dropChildren},
  } = context;
  const {data = {}, tag} = jsonNode;
  const isTextHolderNode = DomTypeUtils.isTextHolder(tag, data);
  const hasDynBoundHtml = DomTypeUtils.isDynBoundHtml(data);
  const isTypographic = DomTypeUtils.isTypographic(tag, data);
  const hasVProp = jsonNode.hasOwnProperty('v');

  const onWarning = (err: any) => {
    reportWarning &&
      reportWarning(
        'ExpandContentNodeError',
        'Failed to expand content nodes. ' + String(err)
      );
  };

  // Expand and return the expanded subtree [node, ...subtreeJsonNodes] for concatenation
  const _expandContentNodes = () => {
    const v = jsonNode.v;
    const isRichTextNode = Boolean(tag && DomTypeUtils.isRichBlock(tag, data));
    let expandedSubtreeJsonNodes = [jsonNode];
    const convertedSubtreeNodes = rteHtmlToJsonNodes(v, {
      removeRootLevelSpaces: isRichTextNode,
      jsonNodes,
      onError: onWarning,
    });

    if (convertedSubtreeNodes.jsonContentNodes.length) {
      // Drop the micro nodes from the original node

      __DEPRECATED__dropChildren(jsonNode);
      // Attach the new subtree
      jsonNode.children = convertedSubtreeNodes.rootChildIds;
      // Append the subtree nodes to the flat nodelist we're building
      expandedSubtreeJsonNodes = expandedSubtreeJsonNodes.concat(
        convertedSubtreeNodes.jsonContentNodes
      );
    }

    // Delete the legacy v value
    // eslint-disable-next-line webflow/no-delete
    delete jsonNode.v;

    return expandedSubtreeJsonNodes;
  };

  return {
    needed: isTextHolderNode && !hasDynBoundHtml && hasVProp,
    logData: {
      msg: 'legacy TextHolder needs expansion',
      meta: {
        nodeId: jsonNode._id,
      },
    },
    apply: () => {
      if (jsonNode.v) {
        const newSubtree = _expandContentNodes();

        // Note: These 'valid tags' will eventually will be enforced in RTE-next 2.x for Typographic
        // nodes when doing away with this conversion on the front-end entirely.
        // We also have a separate patch for nodes that were expanded before this was added here.

        // We want to make sure that any invalid typographic(not rich-text nested) content nodes
        // are converted to spans.
        if (isTypographic) {
          newSubtree.slice(1).forEach((child) => {
            if (
              !child.text &&
              !VALID_TYPOGRAPHIC_CONTENT_NODES.includes(child.tag)
            ) {
              child.tag = 'span';
            }
          });
        }

        return newSubtree;
      } else {
        // Delete the legacy v value if it was falsey
        // eslint-disable-next-line webflow/no-delete
        delete jsonNode.v;
      }
    },
  };
};
